import {
  ApplicantRaceValues,
  Citizenship,
  IdentityVerificationVersion,
  IStreetAddress,
  LoanApplication,
  PersonaParty,
  PersonaVerificationRecord,
} from "@oneethos/shared"
import { FormInput } from "."
import { StateOptions, AddressInputs, SelectYesNo } from "../../components/forms"
import {
  SmsConsentDisclosure,
  SMS_CONSENT_PHONE_LABEL,
} from "../../components/sms-consent-disclosure"
import { PrequalHelperVariant, useAppState, usePrequalPasteHelper } from "../../hooks"
import IdentityVerification, { isPersonaVerificationComplete } from "./IdentityVerification"

type ApplicantFormProps = {
  applicant: Partial<LoanApplication>
  update: (data: string | Record<string, unknown>, value?: unknown) => void
  onAddressChange: (address: IStreetAddress) => void
  address: IStreetAddress
  disableCoreChanges?: boolean
  party?: PersonaParty
  loanApplicationId?: string
  verification?: PersonaVerificationRecord
  onVerificationUpdated?: (lapp: LoanApplication) => void
  onBeforeVerify?: () => Promise<boolean>
  identityVerificationVersion?: IdentityVerificationVersion
}

type PostVerificationFieldsProps = {
  applicant: Partial<LoanApplication>
  update: (data: string | Record<string, unknown>, value?: unknown) => void
  onAddressChange: (address: IStreetAddress) => void
  address: IStreetAddress
}

const PostVerificationFields = ({
  applicant,
  update,
  onAddressChange,
  address,
}: PostVerificationFieldsProps) => (
  <>
    <div className="row">
      <div className="col-md-6">
        <FormInput
          field='driversLicenseNo'
          value={applicant.driversLicenseNo || ''}
          onChange={s => update('driversLicenseNo', s)}
        />
      </div>
      <div className="col-md-6">
        <div className="form-group">
          <label>Drivers License Issuing State</label>
          <select
            className="form-select"
            value={applicant.driversLicenseIssuingState}
            onChange={ev => update('driversLicenseIssuingState', ev.target.value)}
          >
            <option>Choose state...</option>
            <StateOptions />
          </select>
        </div>
      </div>
    </div>
    <div className="row">
      <div className="col-md-6">
        <FormInput
          field='driversLicenseIssueDate'
          value={applicant.driversLicenseIssueDate}
          onChange={s => update('driversLicenseIssueDate', s)}
        />
      </div>
      <div className="col-md-6">
        <FormInput
          field='driversLicenseExpirationDate'
          value={applicant.driversLicenseExpirationDate}
          onChange={s => update('driversLicenseExpirationDate', s)}
        />
      </div>
    </div>
    <div className="form-group">
      <label>Citizenship</label>
      <select
        className="form-select"
        value={applicant.citizenship || ''}
        onChange={ev => update('citizenship', ev.target.value)}
      >
        <option>Choose Citizenship...</option>
        {Citizenship.options().map(([i, label]) => <option key={i} value={i}>{label}</option>)}
      </select>
    </div>
    <div className="form-group">
      <label>Race</label>
      <select
        className="form-select"
        value={applicant.race || ''}
        onChange={ev => update('race', ev.target.value)}
      >
        <option>Choose Race...</option>
        {ApplicantRaceValues.map(r => <option key={r} value={r}>{r}</option>)}
      </select>
    </div>
    <FormInput
      field='statedHouseholdIncome'
      value={applicant.statedHouseholdIncome || ''}
      onChange={s => update('statedHouseholdIncome', s)}
    />
    <div className="form-group">
      <label>Do you currently have a mortgage in your name at any property?</label>
      <SelectYesNo
        value={applicant.hasMortgage || ''}
        onChange={val => update('hasMortgage', val)}
      />
    </div>
    <AddressInputs
      address={address}
      label="Home Address"
      onChange={address => onAddressChange(address)}
    />
  </>
)

export const ApplicantForm = ({
  applicant,
  update,
  onAddressChange,
  address,
  disableCoreChanges,
  party = 'borrower',
  loanApplicationId,
  verification,
  onVerificationUpdated,
  onBeforeVerify,
  identityVerificationVersion,
}: ApplicantFormProps) => {
  // this should only be necessary for coborrower info and n/a for primary borrower
  usePrequalPasteHelper(update, PrequalHelperVariant.CoborrowerInfo)
  const { tenant } = useAppState()
  const usesPersona =
    (identityVerificationVersion ?? applicant.identityVerificationVersion) === 'persona' &&
    !tenant.config?.personaDisabled
  const showPostVerificationFields =
    !usesPersona ||
    isPersonaVerificationComplete(verification)

  // prequal never captures the co-borrower phone, so it stays editable until Persona
  // verification completes; the borrower phone is locked once it exists on this form
  const isCoborrower = party === 'co-borrower'
  const coborrowerVerified = isCoborrower && isPersonaVerificationComplete(verification)
  const phoneLocked = coborrowerVerified
    ? !!applicant.phone
    : !isCoborrower && !!applicant.phone && !!disableCoreChanges
  const showsIdentityVerification = usesPersona && !!loanApplicationId && !!onVerificationUpdated
  // the disclosure covers the SMS that Verify Identity sends, so it only belongs on the
  // screens that render that action; the borrower consents during prequal instead
  const showSmsConsent = showsIdentityVerification && isCoborrower && !coborrowerVerified

  return (
    <>
      <div className="row">
        <div className="col-md-4">
          <FormInput
            field='firstName'
            value={applicant.firstName}
            disabled={disableCoreChanges}
            onChange={s => update('firstName', s)}
          />
        </div>
        <div className="col-md-2">
          <FormInput
            field='middleName'
            value={applicant.middleName}
            disabled={disableCoreChanges}
            emptyOk
            onChange={s => update('middleName', s)}
          />
        </div>
        <div className="col-md-6">
          <FormInput
            field='lastName'
            disabled={disableCoreChanges}
            value={applicant.lastName}
            onChange={s => update('lastName', s)}
          />
        </div>
      </div>
      <FormInput
        field='ssn'
        value={applicant.ssn || ''}
        onChange={s => update('ssn', s)}
      />
      <FormInput
        field='phone'
        label={showSmsConsent ? SMS_CONSENT_PHONE_LABEL : undefined}
        value={applicant.phone || ''}
        onChange={s => update('phone', s)}
        disabled={phoneLocked}
      />
      {showSmsConsent ? <SmsConsentDisclosure actionLabel="Verify Identity" /> : null}
      <FormInput
        field='email'
        value={applicant.email || ''}
        onChange={s => update('email', s)}
        disabled={disableCoreChanges}
      />
      <FormInput
        field='birthDate'
        value={applicant.birthDate || ''}
        onChange={s => update('birthDate', s)}
        disabled={disableCoreChanges}
      />
      {showsIdentityVerification ? (
        <IdentityVerification
          party={party}
          loanApplicationId={loanApplicationId}
          verification={verification}
          onUpdated={onVerificationUpdated}
          onBeforeVerify={onBeforeVerify}
        />
      ) : null}
      {showPostVerificationFields ? (
        <PostVerificationFields
          applicant={applicant}
          update={update}
          onAddressChange={onAddressChange}
          address={address}
        />
      ) : null}
    </>
  )
}

export default ApplicantForm
